import type { ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import { Box, Row, media } from '@simplywallst/ui-core';
import { getPath } from '@/router/utils';
import { ROUTE_ARTICLE } from '@/constants/routes';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import styled from 'styled-components';
import { Link } from '@simplywallst/ui-core/next';

const Author = ({
  author,
  authorSlug,
  authorAvatarUrl,
  publishedDate,
}: {
  authorSlug: string;
  author: string;
  authorAvatarUrl: string;
  publishedDate: string;
}) => {
  return (
    <div className="grid grid-flow-col items-center gap-3.5 text-xs text-soft">
      <div className="grid grid-flow-col items-center gap-1">
        <img
          src={authorAvatarUrl}
          alt={`${author} avatar`}
          data-cy-id="article-card-author-avatar"
          className="mr-1 size-6 rounded-full object-cover"
        />
        <a
          href={`https://simplywall.st/news/about-simply-wall-st-news/${authorSlug}`}
          data-cy-id="article-card-author-link"
          target="_blank"
          rel="noreferrer"
          onClick={(event) => event.stopPropagation()}
          className="text-[inherit] no-underline"
        >
          {author}
        </a>
      </div>
      <time dateTime={publishedDate} data-cy-id="article-card-time">
        {publishedDate}
      </time>
    </div>
  );
};

// =======================

const Excerpt = ({ excerpt, slug }: { excerpt: string; slug: string }) => {
  const { trackEvent } = useTracking();

  return (
    <div className="grid grid-rows-[min-content_min_content] gap-1 px-5 pb-3 pt-4 text-xs">
      <div className="line-clamp-[3] text-solid lg:line-clamp-[3]">
        {excerpt}
      </div>
      <Link
        href={getPath({
          type: ROUTE_ARTICLE,
          payload: { slug },
        })}
        onClick={() => {
          trackEvent({
            modifier: 'market-latest-article',
            action: 'click',
            slug,
          });
        }}
        className="no-underline"
      >
        <span className="">Continue reading</span>
      </Link>
    </div>
  );
};

// =======================

const TitleContainer = styled('div')`
  > div {
    display: grid;
    place-content: end;
    justify-content: start;
    min-height: 210px;

    ${media.mdDown`
      min-height: 128px;
    `}
  }
`;

const Container = styled(Row)`
  position: relative;
  width: 100%;
  background: ${({ theme }) => theme.color.charcoal};
`;

const Gradient = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  width: 101%;
  height: 100%;
  background: ${({ theme }) => theme.color.charcoalRGBA(0.8)};
  mix-blend-mode: normal;
  z-index: 1;
`;

const Content = styled('section')`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const CoverContainer = styled('div')`
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  ${media.mdDown`
    border-radius: 0;
  `}
`;

const CoverImage = styled('img')`
  object-fit: cover;
  height: 100%;
  width: 100%;
`;

const ImageHeader = ({
  imageSource,
  imageSourceSet,
  alt = 'Header cover image',
  children,
  width,
  height,
}: {
  alt?: string;
  imageSource?: string;
  imageSourceSet?: string;
  width?: number;
  height?: number;
  children: ReactNode;
  className?: string;
}) => {
  return (
    <Container className="px-5 py-2">
      <CoverContainer>
        <Gradient />
        {typeof imageSource === 'string' && imageSource !== '' && (
          <CoverImage
            data-cy-id="header-cover-image"
            src={imageSource}
            srcSet={imageSourceSet}
            alt={alt}
            width={width}
            height={height}
          />
        )}
      </CoverContainer>
      {typeof children !== 'undefined' && <Content>{children}</Content>}
    </Container>
  );
};

export const ArticleSummary = ({
  title,
  author,
  authorAvatarUrl,
  authorSlug,
  imageUrl,
  excerpt,
  slug,
  publishedDate,
}: {
  slug: string;
  title: string;
  excerpt: string | null;
  imageUrl: string | null;
  publishedDate: string;
  author?: string | null;
  authorAvatarUrl?: string | null;
  authorSlug?: string | null;
}) => {
  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'ArticleSummary',
  });
  const { srcSet: coverImageSrcSet } = getResponsiveImage({
    src: imageUrl,
    breakpoints: [
      { width: 1024, height: 152 },
      { width: 1023, height: 135 },
      { width: 768, height: 135, fit: 'contain' },
      { width: 425, height: 135, fit: 'contain' },
      { width: 375, height: 135, fit: 'contain' },
    ],
  });

  const coverImageSrc = useCloudflareImage({
    src: imageUrl,
    width: 470,
    height: 210,
    fit: 'cover',
  });

  return (
    <Track>
      <div className="mb-2 grid h-full grid-rows-[auto_1fr] overflow-hidden rounded-lg bg-surface-1">
        <TitleContainer>
          <ImageHeader
            imageSource={coverImageSrc}
            imageSourceSet={coverImageSrcSet}
          >
            <h2 className="m-0 text-sm font-medium">
              <RouterLink
                noUnderline
                to={{ type: ROUTE_ARTICLE, payload: { slug } }}
                onClick={() => {
                  trackEvent({
                    modifier: 'market-latest-article',
                    action: 'click',
                    slug,
                  });
                }}
                data-cy-id="market-page-title"
                className="text-pretty text-[inherit]"
              >
                {title}
              </RouterLink>
            </h2>
            <Box marginBottom="x1" marginTop="x1">
              <Row>
                {author && authorSlug && authorAvatarUrl && (
                  <Author
                    author={author}
                    authorSlug={authorSlug}
                    authorAvatarUrl={authorAvatarUrl}
                    publishedDate={publishedDate}
                  />
                )}
              </Row>
            </Box>
          </ImageHeader>
        </TitleContainer>
        {excerpt && <Excerpt excerpt={excerpt} slug={slug} />}
      </div>
    </Track>
  );
};
